import { Item, Step6ApiRes } from '@/components/MultiStepForm/types';
import ReactGA from 'react-ga4';

const getParams = () => new URLSearchParams(window.location.search);
const getHookUrl = () => getParams().get('hookUrl') || '';

type ElbridgeResult = {
  CONFIGURATION_URL: string;
  ITEM: ElBridgeItem[];
};

type ElBridgeItem = {
  enumber?: string;
  quantity?: string;
  SUPPLIER_ID_DUNS?: string;
  SUPPLIER_ID_GLN?: string;
  MANUFACTURER_PID: string;
  QUANTITY: string;
  REFNUMBER_CONFIG?: string;
  DESCRIPTION_SHORT?: string;
  MANUFACTURER_TYPE_DESCR?: string;
  PRICE_AMOUNT?: string;
  CURRENCY?: string;
  PRICE_QUANTITY?: string;
  ['UDX.EDXF.DISCOUNT_GROUP_MANUFACTURER']?: string;
  ORDER_UNIT?: string;
  INTERNATIONAL_PID?: string | null;
};

type EsItem = { enumber: string; quantity: string };

const ELBRIDGE_LIVE_HUB_URL = 'https://interface.elbridge.etim-deutschland.de/';

const ConfiguratorDefinitions: {
  wid: string;
  identifier: string;
  distributor: boolean;
  endpointHash: string;
  displayName: string;
  extendedElbridge: boolean;
  redirectUrl: string | null;
  blankAddToCart: boolean;
  elbridgeItemMapper: (configurationItem: Item) => ElBridgeItem;
  activator: () => boolean;
}[] = [
  {
    wid: 'internal_tests',
    identifier: 'internal_tests', 
    distributor: false,
    endpointHash:
      'f4586ad10ac6b76146c1bf0e0755a8539d313caaa73d777c2eb27b8f41b17b23',
    displayName: '',
    extendedElbridge: false,
    redirectUrl: null,
    blankAddToCart: false,
    elbridgeItemMapper: function (configurationItem) {
      let item: ElBridgeItem = {
        SUPPLIER_ID_GLN: '8712423008878',
        MANUFACTURER_PID: configurationItem.cr,
        QUANTITY: String(configurationItem.adjustedQuantity) + ".00",
        REFNUMBER_CONFIG: configurationItem.cr,
        DESCRIPTION_SHORT: configurationItem.crDescription,
        ORDER_UNIT: "C62",
      };
      var configuratorDefinition = getConfiguratorDefinition();

      if (configuratorDefinition?.extendedElbridge) {
        item.MANUFACTURER_TYPE_DESCR = configurationItem.priceGroup;
        item.PRICE_AMOUNT = configurationItem.price
          .split('€ ')[1]
          .replace(',', '.');
        item.CURRENCY = 'EUR';
        item.PRICE_QUANTITY = '1';
        item['UDX.EDXF.DISCOUNT_GROUP_MANUFACTURER'] =
          configurationItem.priceGroup;
        item.ORDER_UNIT = 'C62';
      }

      return item;
    },
    activator: () => {  return getParams().get('distributor') === 'internal_tests'},
  },
  {
    wid: 'rexelnl',
    identifier: 'rexelnl',
    distributor: true,
    endpointHash:
      '6472d5c1677640d8b00fd0e5c1357c2b5157f81193012b063134bc027ddc65fe',
    displayName: 'Rexel',
    extendedElbridge: true,
    redirectUrl: null,
    blankAddToCart: false,
    elbridgeItemMapper: function (configurationItem) {
      let item: ElBridgeItem = {
        SUPPLIER_ID_DUNS: '331716014',
        MANUFACTURER_PID: configurationItem.cr,
        QUANTITY: String(
          configurationItem.adjustedQuantity *
            configurationItem.rexelQuantityMultiplier
        ),
        INTERNATIONAL_PID: configurationItem.ean,
        MANUFACTURER_TYPE_DESCR: configurationItem.crDescription,
        ORDER_UNIT: configurationItem.contentUnits,
      };

      return item;
    },
      activator: () => {  return getParams().get('distributor') === 'rexelnl'},
    },
  {
    wid: 'technischeunie_nl',
    identifier: 'technischeuni', 
    distributor: true,
    endpointHash:
      '86f56a51090a64e72882fc6f54eeb6326f77e56700c2584aacd181607d96932c',
    displayName: 'Technische Unie',
    extendedElbridge: false,
    redirectUrl:
      'https://www.technischeunie.nl/redirect/elbridgeRedirect.jsp?toolId=lt14600003',
    blankAddToCart: true,
    elbridgeItemMapper: function (configurationItem) {
      let item: ElBridgeItem = {
        SUPPLIER_ID_GLN: '8712423008878',
        MANUFACTURER_PID: configurationItem.cr,
        QUANTITY: String(configurationItem.adjustedQuantity),
        REFNUMBER_CONFIG: configurationItem.cr,
        DESCRIPTION_SHORT: configurationItem.crDescription,
        ORDER_UNIT: 'C62',
      };


      return item;
    },
    activator: () => getHookUrl().indexOf('www.technischeunie.nl') >= 0 
  },
];

const getConfiguratorDefinition = () => {
  for (let configuratorDefinition of ConfiguratorDefinitions) {
    if (configuratorDefinition.activator()) return configuratorDefinition;
  }

  return ConfiguratorDefinitions[0];
};

function generateResult(overview: Step6ApiRes, isEs: true): EsItem[];
function generateResult(overview: Step6ApiRes, isEs: false): ElBridgeItem[];

function generateResult(overview: Step6ApiRes, isEs: boolean): EsItem[] | ElBridgeItem[] {
  const addedItems: ElBridgeItem[] = [];

  const { subprojects, configurations, pendants } = overview;
  const allConfigurations = [...subprojects, ...configurations, ...pendants];
  const configuratorDefinition = getConfiguratorDefinition();

  if (!configuratorDefinition) {
    return isEs ? [] : []
  }

  for (const configuration of allConfigurations) {
    for (const configurationItem of configuration.items) {
      const elBridgeItem = configuratorDefinition.elbridgeItemMapper(configurationItem);
      const existing = addedItems.find((ai) => ai.MANUFACTURER_PID === configurationItem.cr)

      if (existing) {
        const current = Number.parseFloat(existing.QUANTITY);
        const updated = current + configurationItem.adjustedQuantity;

        existing.QUANTITY = updated.toFixed(2);
      } else {
        addedItems.push(elBridgeItem)
      }
    }
  }

  if (isEs) {
    return addedItems.map((ai) => ({
      enumber: ai.MANUFACTURER_PID,
      quantity: ai.QUANTITY
    }));
  }

  return addedItems;
}

const createHiddenInput = (name: string, value: string | null) => {
  const input = document.createElement('input');

  input.setAttribute('name', name);
  input.setAttribute('value', value || '');
  input.setAttribute('type', 'hidden');

  return input;
};

const getConfigurationUrlForElbridge = () => {
  const url = new URL(window.location.href);

  url.searchParams.delete('hookUrl');
  url.searchParams.delete('hookurl');

  return url.toString();
};

const submitFormNewTab = (data: { result: ElbridgeResult }, actionUrl?: string) => {
  const configuratorDefinition = getConfiguratorDefinition(); // @ts-ignore
  if (!configuratorDefinition) return;

  const params = getParams();

  const resolvedActionUrl = (actionUrl ?? params.get('hookUrl') ?? '')
    .replace(/&amp;/g, '&')
    .trim();
  
  if (!resolvedActionUrl) return;

  const form = document.createElement('form');
  form.method = 'post';
  form.name = 'start';
  form.action = resolvedActionUrl;
  form.enctype = 'multipart/form-data';
  form.acceptCharset = 'UTF-8';

  if (configuratorDefinition.blankAddToCart) {
    form.target = '_blank';
  }
  
  form.appendChild(createHiddenInput('version', params.get('version') ?? '2.0'));
  form.appendChild(createHiddenInput('language', params.get('language') ?? 'en'));
  form.appendChild(createHiddenInput('country', params.get('country') ?? 'SE'));

  form.appendChild(createHiddenInput('result', JSON.stringify(data.result)));
  document.body.appendChild(form);
  form.submit();

  if (configuratorDefinition.redirectUrl) {
    window.location.href = configuratorDefinition.redirectUrl;
  }
};

const getRandomTransactionId = () => {
  const MaxNumber = 100000;
  const tid = Math.floor(Math.random() * MaxNumber);
  const tidString = `T_${tid}-${Date.now()}`;
  return tidString;
};

const addToCartViaElbridge = (overview: Step6ApiRes) => {
  if (overview) {
    const isEs = window.location.href.includes('distributor=es');

    if (isEs) {
      const resultEs = generateResult(overview, true);

      if (!resultEs || resultEs.length <= 0) return;

      const cartData = { products: resultEs };
      window.postMessage(cartData, '*');
      window?.parent?.postMessage(cartData, '*');

      const randomTID = getRandomTransactionId();

      let totalPriceStr = overview.projectSummary.totalPrice || '';
      if (totalPriceStr.indexOf(',') >= 0) {
        totalPriceStr = totalPriceStr.split(',')[0];
      }

      totalPriceStr = totalPriceStr.replace(/[^0-9.-]+/g, '');

      const totalPriceNum = Number.parseFloat(totalPriceStr.split('.').join(''));

      const productsArray = cartData.products.map((p) => ({
        item_id: p.enumber,
        quantity: Number(p.quantity),
      }));

      ReactGA.event({
        category: 'CableTracker.purchase',
        action: 'complete',
        label: totalPriceStr,
      });

      ReactGA.event('purchase', {
        transaction_id: randomTID,
        value: totalPriceNum,
        currency: 'EUR',
        items: productsArray,
      });

      return;
    } 

    const items = generateResult(overview, false); 

    if (!items || items.length <= 0) return;

    const elbridgeResult: ElbridgeResult = {
      CONFIGURATION_URL: getConfigurationUrlForElbridge(),
      ITEM: items,
    };

    submitFormNewTab({ result: elbridgeResult });
  }
};

export const addToCartViaElbridgeHub = (overview: Step6ApiRes) => {
  if (!overview) return;

  const items = generateResult(overview, false);
  if (!items || items.length <= 0) return;

  const elbridgeResult: ElbridgeResult = {
    CONFIGURATION_URL: getConfigurationUrlForElbridge(),
    ITEM: items
  };

  submitFormNewTab({ result: elbridgeResult }, ELBRIDGE_LIVE_HUB_URL);
};

export default addToCartViaElbridge;
